/**
 * Module valuation-property defines property inputs and land valuation results, including
 * land descriptions, planning constraints, structures, areas, property features, and
 * encumbrances.
 *
 * Rental agreements and tenant aggregates are defined in valuation-tenants.ts.
 */
import { z } from 'zod';
import { ISODateSchema, UUIDSchema } from './common';
import { AreaValueSchema } from './measurements';
import { MonetaryValueSchema } from './money';
import { RoundingStrategySchema, ValuationMethodSchema } from './valuation-primitives';
import { TenantSchema } from './valuation-tenants';
const MonumentProtectionSchema = z.object({
    architectural_monument: z.boolean().optional(),
    land_monument: z.boolean().optional(),
    ensemble: z.boolean().optional(),
    statement_date: ISODateSchema.optional(),
    description: z.string().optional(),
});
const LandUsePlanSchema = z.object({
    name_or_number: z.string().optional(),
    usage_type: z.string().array().optional(),
    effective_date: ISODateSchema.optional(),
    description: z.string().optional(),
});
const ZoningPlanSchema = z.object({
    name_or_number: z.string().optional(),
    usage_type: z.string().array().optional(),
    effective_date: ISODateSchema.optional(),
    description: z.string().optional(),
});
const PermissibilitySchema = z.object({
    according_to_paragraph_34: z.string().optional(),
    according_to_paragraph_35: z.string().optional(),
});
const PermittedUseSchema = z.object({
    usage_type: z.string().optional(),
    description: z.string().optional(),
});
const BuildingRegulationSchema = z.object({
    monument_protection: MonumentProtectionSchema.optional(),
    building_permit: z.string().optional(),
    urban_development_regulation: z.string().optional(),
    consolidation_procedure: z.string().optional(),
    land_regulation_text: z.string().optional(),
    detailed_description: z.string().optional(),
    land_use_plan: LandUsePlanSchema.optional(),
    zoning_plan: ZoningPlanSchema.optional(),
    permissibility: PermissibilitySchema.optional(),
    type_and_extent_of_permitted_use: PermittedUseSchema.optional(),
    encumbrances: z.string().optional(),
    change_from_previous_valuation: z.string().optional(),
});
const LandContaminationSchema = z.object({
    statement_available: z.boolean().optional(),
    statement_date: ISODateSchema.optional(),
    contamination_present: z.string().optional(),
    description: z.string().optional(),
});
export const LandDescriptionSchema = z.object({
    building_regulation: BuildingRegulationSchema.optional(),
    land_contamination: LandContaminationSchema.optional(),
    general_note: z.string().optional(),
    change_from_previous_valuation: z.string().optional(),
});
export const StandardLandValueZoneSchema = z.object({
    uuid: UUIDSchema,
    index: z.number(),
    municipality: z.string().optional(),
    cadastral_district: z.string().optional(),
    standard_land_value_reference_zone_number: z.string().optional(),
    standard_land_value_reference_zone_name: z.string().optional(),
    title: z.string().optional(),
    standard_land_value: MonetaryValueSchema.optional(),
    standard_land_value_reference_date: ISODateSchema.optional(),
    usage_type: z.string().optional(),
    usage_type_addition: z.string().optional(),
    development_status: z.string().optional(),
    contribution_and_tax_legal_status: z.string().optional(),
    procedural_legal_status: z.string().optional(),
    floor_area_ratio: z.number().optional(),
    plot_ratio: z.number().optional(),
    full_stories_number: z.string().optional(),
    construction_type: z.string().optional(),
    plot_size: AreaValueSchema.optional(),
    plot_depth: z.number().optional(),
    field_productivity_index: z.number().optional(),
    grassland_index: z.number().optional(),
    text: z.string().optional(),
    remarks: z.string().optional(),
});
const LifeSpanSchema = z.object({
    total_economic: z.number(),
    calculated_residual: z.number(),
    custom_residual: z.number().optional(),
    construction_year: z.number().optional(),
    fictional_construction_year: z.number().optional(),
    depreciation_factor: z.number().optional(),
    description: z.string().optional(),
    age: z.number().optional(),
});
export const BaseValuationStructureSchema = z.object({
    title: z.string(),
    structure_uuid: UUIDSchema,
    gross_floor_area: AreaValueSchema,
    living_area: AreaValueSchema,
    usage_area: AreaValueSchema,
    living_and_usage_area: AreaValueSchema.optional(),
    life_span: LifeSpanSchema,
    construction_year_description: z.string(),
});
const BkiScopeSchema = z.enum(['gross_floor_area', 'unit', 'usage_area']);
const BkiSchema = z.object({
    scope: BkiScopeSchema,
    min: MonetaryValueSchema.optional(),
    average: MonetaryValueSchema.optional(),
    max: MonetaryValueSchema.optional(),
    assigned_value: MonetaryValueSchema,
    description: z.string().optional(),
});
export const ValuationStructureSchema = BaseValuationStructureSchema.extend({
    tenantsV3: TenantSchema.array(),
    valuation_area_uuid: UUIDSchema.optional(),
    bki: BkiSchema.optional(),
});
const LandAdjustmentFactorsSchema = z.object({
    economy_factor: z.number().optional(),
    type_of_usage: z.number().optional(),
    development_status: z.number().optional(),
    conversion_factor: z.number().optional(),
    site_coverage_ratio: z.number().optional(),
    number_of_full_floors: z.number().optional(),
    construction_method: z.number().optional(),
    plot_size: z.number().optional(),
    plot_depth: z.number().optional(),
    layout_factor: z.number().optional(),
    location_characteristics: z.number().optional(),
    topography: z.number().optional(),
    environmental_influences: z.number().optional(),
    agricultural_index: z.number().optional(),
    grass_land_index: z.number().optional(),
    other_adjustments_factor: z.number().optional(),
    total_adjustment_factor: z.number().optional(),
});
const StandardLandValueSchema = z.object({ value: MonetaryValueSchema });
const ValuationAreaSchema = z.object({
    uuid: UUIDSchema,
    title: z.string(),
    area_value: AreaValueSchema,
    standard_land_value: StandardLandValueSchema,
    land_value: MonetaryValueSchema,
    description: z.string().optional(),
    is_yielding: z.boolean(),
    adjustment_factors: LandAdjustmentFactorsSchema,
    market_adjustment_factor: z.number(),
    mortgage_lending_market_adjustment_factor: z.number(),
    market_adjusted_land_value: MonetaryValueSchema.optional(),
});
export const ValuationLandSchema = z.object({
    areas: ValuationAreaSchema.array(),
    market_user_defined_value: MonetaryValueSchema.optional(),
    mortgage_user_defined_value: MonetaryValueSchema.optional(),
});
const SpecialPropertyFeatureTypeSchema = z.enum([
    'special_earnings',
    'construction_defects',
    'economic_obsolescence',
    'stripping_costs',
    'land_pollution',
    'outstanding_condition',
    'rights_restrictions',
    'miscellaneous',
]);
const SpecialPropertyFeatureSubTypeSchema = z.enum([
    'overrent',
    'underrent',
    'vacancy_deduction',
    'fit_out_costs',
    'solar_pv_system',
    'maintenance_backlog',
    'construction_defects',
    'commercial_depreciation',
    'demolition_costs',
    'demolition_and_clearance_costs',
    'deconstruction_costs',
    'soil_contamination',
    'legacy_pollution',
    'residential_right',
    'usufruct',
    'utility_easement',
    'right_of_way',
    'tenant_servitude',
    'usage_restrictions',
    'heritable_building_right',
    'encroachment',
    'building_restrictions',
    'real_burden',
    'development_costs',
    'usual_extract',
    'capex',
    'budgeted_actions',
    'build_out_costs',
    'accruals',
]);
export const SpecialPropertyFeatureSchema = z.object({
    feature_type: SpecialPropertyFeatureTypeSchema,
    sub_type: SpecialPropertyFeatureSubTypeSchema.optional(),
    value: MonetaryValueSchema,
    market_valuation_methods: ValuationMethodSchema.array(),
    mortgage_lending_valuation_methods: ValuationMethodSchema.array(),
    description: z.string(),
    market_valuation_land_areas_ids: UUIDSchema.array(),
    mortgage_lending_valuation_land_areas_ids: UUIDSchema.array(),
});
const GenderSchema = z.enum(['male', 'female']);
const BeneficiarySchema = z.object({ age: z.number(), gender: GenderSchema });
const PaymentTypeSchema = z.enum(['in_advance', 'in_arrears']);
const EncumbranceCalculationTypeSchema = z.enum(['on_the_longer_life', 'on_the_shorter_life']);
export const EncumbranceSchema = z.object({
    value: MonetaryValueSchema.optional(),
    net_present_value_factor: z.number().optional(),
    feature_type: z.string(),
    included_in_market_valuation: z.boolean(),
    included_in_mortgage_valuation: z.boolean().optional(),
    description: z.string(),
    beneficiary1: BeneficiarySchema,
    beneficiary2: BeneficiarySchema.optional(),
    periodic_payment_amount: MonetaryValueSchema,
    payments_per_year: z.number(),
    interest_rate: z.number(),
    interest_rate_periods_per_year: z.number(),
    payment_type: PaymentTypeSchema,
    calculation_type: EncumbranceCalculationTypeSchema,
});
const ValuationAreaSummarySchema = z.object({
    uuid: UUIDSchema,
    land_value: MonetaryValueSchema,
    market_adjusted_land_value: MonetaryValueSchema,
    special_property_features_value: MonetaryValueSchema,
    market_and_special_property_features_adjusted_land_value: MonetaryValueSchema,
});
const YieldLandValuationSchema = z.object({
    valuation_land_areas_values_summaries: ValuationAreaSummarySchema.array(),
    valuation_land_areas: ValuationAreaSchema.array(),
    land_value: MonetaryValueSchema,
    land_value_rounded: MonetaryValueSchema,
    rounding_strategy: RoundingStrategySchema,
    user_defined_value: MonetaryValueSchema.optional(),
});
const LandValuationSchema = z.object({
    yielding: YieldLandValuationSchema,
    non_yielding: YieldLandValuationSchema,
    land_value: MonetaryValueSchema,
    land_value_rounded: MonetaryValueSchema,
});
export const CombinedLandValuationSchema = z.object({
    market: LandValuationSchema,
    mortgage: LandValuationSchema,
});
